import StaticPage from "./StaticPage";

const content = {
  en: {
    title: "Privacy Policy",
    subtitle: "How FileNesto handles your files and personal data.",
    description: "FileNesto Privacy Policy: how we process files in memory, what we store, our use of cookies and analytics, and your rights regarding your data.",
    lastUpdated: "February 2026",
    sections: [
      {
        h2: "1. Who we are",
        body: "FileNesto (\"we\", \"us\") is a digital brand based in Morocco that operates the website filenesto.com and provides free online file processing tools. This Privacy Policy explains what data we collect, how we use it, and the choices you have.",
      },
      {
        h2: "2. Files you upload",
        body: "When you use a tool on FileNesto (such as Merge PDF, Compress PDF, or PDF to Word), the file you upload is sent to our servers, processed in memory, and the result is returned to your browser. We do not save, archive, share, or analyse the contents of your files.\n\nUploaded files are discarded immediately after the response is sent. We do not retain copies on disk, in databases, or on any third-party storage.",
      },
      {
        h2: "3. Personal information we collect",
        body: "FileNesto does not require an account. We do not ask for your name, email, or phone number to use any tool. The only personal information we may handle is what you voluntarily share when contacting us by email.",
      },
      {
        h2: "4. Anonymous usage analytics",
        body: "To understand which tools are most useful and improve the service, we collect anonymous usage statistics — for example, which pages are visited and which tools are used. This is done via Google Analytics 4 only when the corresponding measurement ID is configured. These tools may set cookies and collect technical data such as browser type, device type, approximate location, and pages viewed. They do not identify you personally.\n\nYou can opt out of analytics cookies through your browser settings or browser-level Do Not Track preferences.",
      },
      {
        h2: "5. Advertising",
        body: "FileNesto may display advertising provided by Google AdSense to keep its tools free. Google may use cookies to serve ads based on your prior visits to FileNesto and other sites. You can learn more and manage your preferences at the Google Ads Settings page.",
      },
      {
        h2: "6. Cookies",
        list: [
          "Functional cookies: store your language preference (filenesto-lang).",
          "Analytics cookies: Google Analytics 4 (anonymous usage, only when configured).",
          "Advertising cookies: Google AdSense (when ads are enabled).",
        ],
      },
      {
        h2: "7. Data security",
        body: "All file uploads and downloads use HTTPS encryption. Files are processed in memory and never written to disk. Operational logs do not include file contents.",
      },
      {
        h2: "8. International users",
        body: "FileNesto is operated from Morocco and serves users worldwide. By using the service, you consent to the transfer and processing of anonymous technical data in accordance with this policy.",
      },
      {
        h2: "9. Your rights",
        body: "Because we do not store personal information beyond anonymous analytics, there is typically no personal data to access, correct, or delete. If you have contacted us by email, you may request deletion of that conversation by writing to contact@filenesto.com.",
      },
      {
        h2: "10. Changes to this policy",
        body: "We may update this Privacy Policy as the service evolves. The \"last updated\" date at the top of this page reflects the most recent changes.",
      },
      {
        h2: "11. Contact",
        body: "Questions about this Privacy Policy? Email us at contact@filenesto.com.",
      },
    ],
  },
  fr: {
    title: "Politique de confidentialité",
    subtitle: "Comment FileNesto gère vos fichiers et vos données personnelles.",
    description: "Politique de confidentialité FileNesto : traitement en mémoire des fichiers, données conservées, utilisation des cookies et analytics, vos droits.",
    lastUpdated: "Février 2026",
    sections: [
      {
        h2: "1. Qui sommes-nous",
        body: "FileNesto (« nous ») est une marque digitale basée au Maroc qui exploite le site filenesto.com et propose des outils en ligne gratuits de traitement de fichiers. Cette politique explique quelles données nous collectons, comment nous les utilisons et les choix dont vous disposez.",
      },
      {
        h2: "2. Fichiers que vous envoyez",
        body: "Lorsque vous utilisez un outil FileNesto (Fusionner PDF, Compresser PDF, PDF vers Word, etc.), le fichier envoyé est transmis à nos serveurs, traité en mémoire, et le résultat est renvoyé à votre navigateur. Nous ne sauvegardons pas, n'archivons pas, ne partageons pas et n'analysons pas le contenu de vos fichiers.\n\nLes fichiers uploadés sont supprimés immédiatement après l'envoi de la réponse. Aucune copie n'est conservée sur disque, en base de données ou chez un tiers.",
      },
      {
        h2: "3. Informations personnelles collectées",
        body: "FileNesto ne nécessite pas de compte. Nous ne demandons ni votre nom, ni votre email, ni votre téléphone pour utiliser un outil. Les seules informations personnelles que nous pouvons traiter sont celles que vous nous communiquez volontairement par email.",
      },
      {
        h2: "4. Statistiques d'usage anonymes",
        body: "Pour comprendre quels outils sont les plus utiles et améliorer le service, nous collectons des statistiques d'usage anonymes — par exemple, les pages visitées et les outils utilisés. Cela passe par Google Analytics 4 uniquement lorsque l'identifiant correspondant est configuré. Ces outils peuvent déposer des cookies et collecter des données techniques telles que le type de navigateur, type d'appareil, localisation approximative et pages vues. Ils ne vous identifient pas personnellement.\n\nVous pouvez désactiver les cookies analytiques via les paramètres de votre navigateur ou la préférence Do Not Track.",
      },
      {
        h2: "5. Publicité",
        body: "FileNesto peut afficher des publicités fournies par Google AdSense pour maintenir ses outils gratuits. Google peut utiliser des cookies pour diffuser des publicités basées sur vos visites antérieures sur FileNesto et d'autres sites. Vous pouvez en savoir plus et gérer vos préférences sur la page Paramètres des annonces Google.",
      },
      {
        h2: "6. Cookies",
        list: [
          "Cookies fonctionnels : enregistrent votre préférence de langue (filenesto-lang).",
          "Cookies analytiques : Google Analytics 4 (usage anonyme, uniquement si configuré).",
          "Cookies publicitaires : Google AdSense (lorsque la publicité est activée).",
        ],
      },
      {
        h2: "7. Sécurité des données",
        body: "Toutes les transmissions de fichiers utilisent un chiffrement HTTPS. Les fichiers sont traités en mémoire et ne sont jamais écrits sur disque. Les journaux opérationnels ne contiennent pas le contenu des fichiers.",
      },
      {
        h2: "8. Utilisateurs internationaux",
        body: "FileNesto est exploité depuis le Maroc et sert des utilisateurs du monde entier. En utilisant le service, vous consentez au transfert et au traitement de données techniques anonymes conformément à cette politique.",
      },
      {
        h2: "9. Vos droits",
        body: "Comme nous ne stockons aucune donnée personnelle au-delà des analytics anonymes, il n'y a généralement pas de données personnelles à consulter, corriger ou supprimer. Si vous nous avez contactés par email, vous pouvez demander la suppression de cet échange en écrivant à contact@filenesto.com.",
      },
      {
        h2: "10. Modifications de cette politique",
        body: "Nous pouvons mettre à jour cette politique au fil de l'évolution du service. La date « dernière mise à jour » en haut de page reflète les modifications les plus récentes.",
      },
      {
        h2: "11. Contact",
        body: "Questions concernant cette politique ? Écrivez-nous à contact@filenesto.com.",
      },
    ],
  },
};

export default function PrivacyPolicy() {
  return <StaticPage content={content} slug="/privacy-policy" testid="page-privacy" />;
}
